import { Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import {
  HomeIcon,
  UsersIcon,
  ComputerDesktopIcon,
  BellIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  KeyIcon,
  XMarkIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/context/AuthContext'
import { useTheme } from '@/context/ThemeContext'
import type { UserRole } from '@/types'

type NavItem = {
  name: string
  href: string
  icon: typeof HomeIcon
  /** Only visible to admins */
  adminOnly?: boolean
  /** Visible to roles that can write (admin + manager) — hidden from viewers */
  writersOnly?: boolean
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Opérateurs', href: '/realtime', icon: ComputerDesktopIcon },
  { name: 'Onboarding', href: '/operators', icon: UsersIcon, writersOnly: true },
  { name: 'Alertes', href: '/alerts', icon: BellIcon },
  { name: 'Rapports', href: '/reports', icon: DocumentChartBarIcon },
  { name: 'Utilisateurs', href: '/users', icon: UserGroupIcon, adminOnly: true },
  { name: 'Paramètres', href: '/settings', icon: Cog6ToothIcon },
]

function visibleNav(role: UserRole | undefined) {
  return navigation.filter((item) => {
    if (item.adminOnly && role !== 'admin') return false
    if (item.writersOnly && role !== 'admin' && role !== 'manager') return false
    return true
  })
}

interface SidebarProps {
  mobileOpen: boolean
  onMobileClose: () => void
  onOpenPasswordModal: () => void
  onLogout: () => void
}

interface SidebarContentProps {
  compact: boolean
  onLinkClick?: () => void
  onOpenPasswordModal: () => void
  onLogout: () => void
}

function SidebarContent({ compact, onLinkClick, onOpenPasswordModal, onLogout }: SidebarContentProps) {
  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()

  return (
    <>
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-gray-200 dark:border-gray-700 ${compact ? 'justify-center px-2' : 'px-6'}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="30" fill="currentColor" opacity="0.4" />
              <circle cx="50" cy="50" r="15" fill="currentColor" />
            </svg>
          </div>
          {!compact && <span className="font-semibold text-gray-900 dark:text-white">SONAR</span>}
        </div>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-4 space-y-1 ${compact ? 'px-2' : 'px-4'}`}>
        {visibleNav(user?.role).map((item) => {
          const isActive = location.pathname === item.href
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onLinkClick}
              title={compact ? item.name : undefined}
              className={`sidebar-link ${isActive ? 'active' : ''} ${compact ? 'justify-center px-2' : ''}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!compact && item.name}
            </Link>
          )
        })}
      </nav>

      {/* User section */}
      <div className={`border-t border-gray-200 dark:border-gray-700 ${compact ? 'p-2' : 'p-4'}`}>
        <button
          onClick={toggleTheme}
          title={compact ? (theme === 'dark' ? 'Mode clair' : 'Mode sombre') : undefined}
          className={`w-full flex items-center gap-3 py-2 mb-3 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${compact ? 'justify-center px-2' : 'px-3'}`}
        >
          {theme === 'dark' ? (
            <SunIcon className="w-5 h-5 flex-shrink-0" />
          ) : (
            <MoonIcon className="w-5 h-5 flex-shrink-0" />
          )}
          {!compact && <span>{theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>}
        </button>

        {!compact && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {user?.fullname?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.fullname}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenPasswordModal}
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <KeyIcon className="w-4 h-4" />
                Mot de passe
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </>
        )}

        {compact && (
          <div className="flex flex-col gap-1">
            <button
              onClick={onOpenPasswordModal}
              title="Mot de passe"
              className="flex justify-center p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <KeyIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onLogout}
              title="Déconnexion"
              className="flex justify-center p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export function Sidebar({ mobileOpen, onMobileClose, onOpenPasswordModal, onLogout }: SidebarProps) {
  return (
    <>
      {/* Desktop: icon rail (lg) and full (xl) */}
      <aside className="hidden lg:flex lg:w-16 xl:w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col flex-shrink-0">
        {/* Compact rail for lg only */}
        <div className="flex-1 flex flex-col xl:hidden">
          <SidebarContent compact={true} onOpenPasswordModal={onOpenPasswordModal} onLogout={onLogout} />
        </div>
        {/* Full sidebar for xl+ */}
        <div className="flex-1 hidden xl:flex xl:flex-col">
          <SidebarContent compact={false} onOpenPasswordModal={onOpenPasswordModal} onLogout={onLogout} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <Transition show={mobileOpen} as={Fragment}>
        <Dialog onClose={onMobileClose} className="relative z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-72 max-w-[85vw] bg-white dark:bg-gray-800 flex-col">
                <button
                  onClick={onMobileClose}
                  className="absolute top-3 right-3 z-10 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
                <SidebarContent
                  compact={false}
                  onLinkClick={onMobileClose}
                  onOpenPasswordModal={() => {
                    onMobileClose()
                    onOpenPasswordModal()
                  }}
                  onLogout={() => {
                    onMobileClose()
                    onLogout()
                  }}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
